import { ErrorLoadingPage } from '@/components/screens/ErrorHandling/ErrorLoadingPage';
import { ADDON_DETAIL_PAGE_PATH } from '@/constants/paths';
import { EventTracker } from '@/utils';
import { Card } from '@design-system/Card';
import { Skeleton } from '@design-system/Skeleton';
import { AddonCard } from '@screens/ShopPage/components/AddonCard';
import { ShopUnavailablePage } from '@screens/ShopPage/components/ShopUnavailablePage';
import { useAddOnOrderNow } from '@screens/ShopPage/useAddOnOrderNow';
import { analyticsEvent } from '@utils/analyticsEvent';
import { getAddOnFormulaImageUrl } from '@utils/nutrition/getFormulaImageUrl';
import { useMergedShopAddon } from '@viome/viome-base/src/usecase/Shop/ShopQuery';
import { useNavigate } from 'react-router-dom';

const ADD_ON_TAB_KEY = 'ADD-ON';

const AddonCardSkeleton = () => (
  <Card className="rounded-2xl p-6 flex flex-col items-center gap-4">
    <Skeleton className="size-20 rounded-full" />
    <Skeleton className="h-6 w-40" />
    <Skeleton className="h-6 w-20" />
    <Skeleton className="h-14 w-full rounded-full" />
  </Card>
);

export const ShopAddOnPage = () => {
  const navigate = useNavigate();
  const { data, isLoading, isError } = useMergedShopAddon();
  const { handleOrderNow, isProcessing } = useAddOnOrderNow();

  const addOnTitle = data?.tabTitle?.[ADD_ON_TAB_KEY];
  const addOnSubtitle = data?.tabSubtitle?.[ADD_ON_TAB_KEY];
  const addOns = data?.addon ?? [];

  const handleViewDetails = (productId: string) => {
    EventTracker.trackEvent('shop', analyticsEvent.SHOP_ADDON_VIEWDETAILS);
    navigate(ADDON_DETAIL_PAGE_PATH.replace(':id', productId));
  };

  if (isLoading) {
    return (
      <div className="space-y-8">
        <AddonCardSkeleton />
      </div>
    );
  }

  if (isError) {
    return (
      <ErrorLoadingPage
        variant="light"
        errorType="error"
        hideBackground
        hideNavBar
      />
    );
  }

  if (!addOns.length) {
    return <ShopUnavailablePage tab="addon" />;
  }

  return (
    <div className="space-y-8" data-testid="shopAddOn-page">
      {(addOnTitle || addOnSubtitle) && (
        <div className="text-center" data-testid="shopAddOn-header">
          {addOnTitle && (
            <h1 className="typography-display2 text-primary">{addOnTitle}</h1>
          )}
          {addOnSubtitle && (
            <p className="typography-title font-serif italic font-light text-primary mt-1">
              {addOnSubtitle}
            </p>
          )}
        </div>
      )}

      {addOns.map((addOn) => (
        <AddonCard
          key={addOn.productId}
          id={addOn.productId}
          title={addOn.title}
          subtitle={addOn.subtitle}
          basePrice={Number(addOn.price)}
          discountAmount={addOn.discountAmount}
          discountPercentage={addOn.discountPercentage}
          description={
            addOn.shortDescription ?? addOn.longDescription ?? ''
          }
          imageSrc={getAddOnFormulaImageUrl(addOn.image_url)}
          packs={addOn.packs}
          trimDecimal={addOn.trimDecimal}
          isProcessing={isProcessing(addOn.sku)}
          onOrderNow={(quantity) => handleOrderNow(addOn, quantity)}
          onViewDetails={() => handleViewDetails(addOn.productId)}
        />
      ))}
    </div>
  );
};
